import { useState, useEffect } from 'react'
import { usePools } from '@/hooks/use-pools'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Zap } from 'lucide-react'
import ControlSignalForm from '@/components/control/ControlSignalForm'
import SignalTimeline from '@/components/control/SignalTimeline'

export default function ControlPage() {
  const { data: pools = [], isLoading: loading } = usePools()
  const [selectedPoolID, setSelectedPoolID] = useState<string | null>(null)

  // Auto-select first pool if none selected
  useEffect(() => {
    if (!selectedPoolID && pools.length > 0) {
      setSelectedPoolID(pools[0].poolID)
    }
  }, [pools, selectedPoolID])

  const selectedPool = pools.find((p) => p.poolID === selectedPoolID) ?? null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-8 w-8" />
            Control Signals
          </h1>
          <p className="text-muted-foreground">Submit power setpoints and manage control tasks</p>
        </div>
      </div>

      {/* Pool Selector */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : pools.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No pools available. Create a pool first.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Select Pool</CardTitle>
              <CardDescription>Choose a pool to manage control signals</CardDescription>
            </CardHeader>
            <CardContent>
              <select
                value={selectedPoolID || ''}
                onChange={(e) => setSelectedPoolID(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pools.map((pool) => (
                  <option key={pool.poolID} value={pool.poolID}>
                    {pool.name} ({pool.poolID})
                  </option>
                ))}
              </select>
            </CardContent>
          </Card>

          {selectedPoolID && selectedPool && (
            <>
              {/* Current Signals */}
              <SignalTimeline
                poolID={selectedPoolID}
                poolName={selectedPool.name}
              />

              {/* Submit New Signals */}
              <ControlSignalForm
                poolID={selectedPoolID}
                poolName={selectedPool.name}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
